import React from 'react';
import { Box, FormGroup, FormLabel, SxProps, Theme } from '@mui/material';
import CustomCheckbox, { CheckboxSize, CheckboxValue } from '../CustomCheckbox';
import { useTheme } from '@mui/material/styles';

export interface CheckboxOption {
  label: React.ReactNode;
  value: CheckboxValue;
  disabled?: boolean;
  size?: CheckboxSize;
}

export interface CheckboxGroupProps {
  options: CheckboxOption[];
  values: CheckboxValue[];
  onChange: (values: CheckboxValue[]) => void;
  label?: React.ReactNode;
  direction?: 'row' | 'column';
  disabled?: boolean;
  name?: string;
  spacing?: number;
  sx?: SxProps<Theme>;
  groupSx?: SxProps<Theme>;
  labelSx?: SxProps<Theme>;
  itemSx?: SxProps<Theme>;
}

const CheckboxGroup: React.FC<CheckboxGroupProps> = ({
  options,
  values,
  onChange,
  label,
  direction = 'column',
  disabled = false,
  name,
  spacing = 1.5,
  sx,
  groupSx,
  labelSx,
  itemSx,
}) => {
  const theme = useTheme();
  const colors = theme.palette;

  const handleItemChange = (isChecked: boolean, changedValue?: CheckboxValue) => {
    if (changedValue === undefined) {
      return;
    }

    const nextValues = isChecked
      ? [...values, changedValue]
      : values.filter((item) => item !== changedValue);

    onChange(nextValues);
  };

  return (
    <Box sx={sx}>
      {label ? (
        <FormLabel
          sx={{
            display: 'block',
            marginBottom: 1,
            fontSize: '14px',
            fontWeight: 500,
            color: colors.checkboxGroup.optionText,
            ...labelSx,
          }}
        >
          {label}
        </FormLabel>
      ) : null}

      <FormGroup
        row={direction === 'row'}
        sx={{
          gap: spacing,
          ...groupSx,
        }}
      >
        {options.map((option) => (
          <CustomCheckbox
            key={String(option.value)}
            label={option.label}
            value={option.value}
            checked={values.includes(option.value)}
            onChange={handleItemChange}
            disabled={disabled || option.disabled}
            size={option.size ?? 'medium'}
            name={name}
            sx={itemSx}
          />
        ))}
      </FormGroup>
    </Box>
  );
};

export default CheckboxGroup;
